/**
 * Pure date-bucketing logic for the staff-task board. No React — unit-testable.
 */

export const DONE = 'done';
export const OPEN = 'open';
export const CANCELLED = 'cancelled';
export const IN_PROGRESS = 'in_progress';

export const PRIORITIES = ['low', 'normal', 'high'] as const;

/** Local-time `YYYY-MM-DD`. */
const pad = (n: number) => String(n).padStart(2, '0');
const fmt = (d: Date): string =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
export const localToday = (): string => fmt(new Date());
export const addDays = (iso: string, n: number): string => {
  const [y, m, d] = iso.split('-').map(Number);
  return fmt(new Date(y, m - 1, d + n));
};

export type Bucket =
  | 'overdue'
  | 'today'
  | 'tomorrow'
  | 'thisWeek'
  | 'later'
  | 'noDate'
  | 'done';

export interface IStaffTask {
  id: string;
  title: string;
  description?: string | null;
  status: string;
  priority: string;
  dueDate: string | null;
  completedAt?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  assigneeName?: string | null;
  createdByName?: string | null;
  group?: { id: string; name: string } | null;
  student?: {
    id: string;
    user?: { firstName?: string | null; lastName?: string | null } | null;
  } | null;
  agreement?: { id: string; agreementTitle: string | null } | null;
}

export const isClosed = (task: IStaffTask): boolean =>
  task.status === DONE || task.status === CANCELLED;

export const studentName = (task: IStaffTask): string =>
  task.student
    ? [task.student.user?.lastName, task.student.user?.firstName]
        .filter(Boolean)
        .join(' ')
    : '';

export const bucketOf = (
  task: IStaffTask,
  today: string,
  tomorrow: string,
  weekEnd: string,
): Bucket => {
  if (isClosed(task)) return 'done';
  const d = task.dueDate;
  if (!d) return 'noDate';
  if (d < today) return 'overdue';
  if (d === today) return 'today';
  if (d === tomorrow) return 'tomorrow';
  if (d <= weekEnd) return 'thisWeek';
  return 'later';
};

export type TBucketMap = Record<Bucket, IStaffTask[]>;

export const emptyBuckets = (): TBucketMap => ({
  overdue: [],
  today: [],
  tomorrow: [],
  thisWeek: [],
  later: [],
  noDate: [],
  done: [],
});

export const bucketTasks = (tasks: IStaffTask[], today: string): TBucketMap => {
  const tomorrow = addDays(today, 1);
  const weekEnd = addDays(today, 7);
  const map = emptyBuckets();
  tasks.forEach((task) => {
    map[bucketOf(task, today, tomorrow, weekEnd)].push(task);
  });
  return map;
};

export type TColumnTone = 'danger' | 'warn';

/**
 * The five always-visible board columns. `noDate` rides in «Позже» after an
 * in-column divider (a 6th column would starve the others on laptop widths);
 * done/cancelled live in a collapsible strip below the board instead.
 */
export const COLUMNS: Array<{ key: Bucket; tone?: TColumnTone }> = [
  { key: 'overdue', tone: 'danger' },
  { key: 'today', tone: 'warn' },
  { key: 'tomorrow' },
  { key: 'thisWeek' },
  { key: 'later' },
];
